import * as React from "react";
import {
  Dialog,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { fonts, semantic, rolesFor, hexToRGBA } from "../../../common/theme/brandTokens";

export type ConfirmVariant = "destructive" | "warning" | "neutral";

interface ConfirmDialogProps {
  open: boolean;
  title: React.ReactNode;
  /** Primary body copy (rendered as the branded dialog text). */
  body?: React.ReactNode;
  /** Optional extra content rendered under the body (e.g. a details box). */
  children?: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onClose: () => void;
  loading?: boolean;
  variant?: ConfirmVariant;
  /** Override the icon chip glyph. */
  icon?: React.ReactNode;
  /** Hide the icon chip entirely (defaults to shown for destructive/warning). */
  hideIcon?: boolean;
}

/**
 * Branded confirmation / destructive dialog (brand-template "Dialog" spec):
 * icon chip (semantic tint), brand-face title, text-2 body, divider before the
 * actions, ghost Cancel + colored confirm. Theme supplies the surface, scrim,
 * radius and shadow.
 */
export default function ConfirmDialog({
  open,
  title,
  body,
  children,
  confirmLabel,
  cancelLabel = "Cancel",
  onConfirm,
  onClose,
  loading = false,
  variant = "destructive",
  icon,
  hideIcon = false,
}: ConfirmDialogProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const r = rolesFor(isDark);

  const sem =
    variant === "warning" ? semantic.warning : variant === "neutral" ? null : semantic.error;
  const confirmColor: "error" | "warning" | "primary" =
    variant === "warning" ? "warning" : variant === "neutral" ? "primary" : "error";
  const chipBg = sem ? (isDark ? hexToRGBA(sem.dark, 0.16) : sem.tint) : hexToRGBA(r.primary, 0.12);
  const chipColor = sem ? (isDark ? sem.dark : sem.main) : r.primary;
  const showIcon = !hideIcon && variant !== "neutral";

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { maxWidth: 400 } }}
    >
      {/* dlg-body */}
      <Box sx={{ p: "20px 22px 8px" }}>
        {showIcon && (
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1.5,
              bgcolor: chipBg,
              color: chipColor,
              "& svg": { fontSize: 20 },
            }}
          >
            {icon ?? <WarningAmberRoundedIcon />}
          </Box>
        )}
        <Typography
          component="h2"
          sx={{
            fontFamily: fonts.brand,
            fontSize: 20,
            fontWeight: 400,
            letterSpacing: "0.0075em",
            color: "text.primary",
            mb: "6px",
          }}
        >
          {title}
        </Typography>
        {body && (
          <Typography sx={{ fontSize: "13.5px", lineHeight: 1.55, color: "text.secondary", m: 0 }}>
            {body}
          </Typography>
        )}
        {children}
        <Box sx={{ height: "1px", mt: "12px", bgcolor: "divider" }} />
      </Box>

      {/* dlg-actions */}
      <DialogActions>
        <Button variant="ghost" onClick={onClose} disabled={loading}>
          {cancelLabel}
        </Button>
        <Button variant="contained" color={confirmColor} onClick={onConfirm} disabled={loading}>
          {loading ? <CircularProgress size={18} color="inherit" /> : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
